//@flow
import queryString from 'query-string';
import {
    LiveViewTabs,
    OrderStatusEnum,
    TripStatusEnum,
    TripViews
} from 'common/enums/LiveViewEnums';
import { hasUserUpgradedToV2 } from 'common/trackIQ/hooks/useIsUpgradedToV2';
import type { Location } from 'react-router-dom';
import { SCREENS } from 'common/trackIQ/components/v2/constants';
import liveViewIds from 'common/trackIQ/tests/testids/liveViewIds.json';

export const isLiveviewUrl = (url: string): boolean => {
    const liveviewPaths = [
        '/live_view/tasks',
        '/live_view/tour',
        '/live_view/riders'
    ];
    return liveviewPaths.some(p => url.includes(p));
};

export const getDefaultLiveViewTab = ({
    isOrderTabEnabled,
    isTourTabEnabled
}: {
    isOrderTabEnabled: boolean,
    isTourTabEnabled: boolean
}) => {
    if (isOrderTabEnabled) {
        return {
            path: `/${LiveViewTabs.Orders}`,
            query: { selectedStatus: OrderStatusEnum.All }
        };
    }
    if (isTourTabEnabled) {
        return {
            path: `/${LiveViewTabs.Trips}`,
            query: {
                selectedStatus: TripStatusEnum.All,
                view: TripViews.Details
            }
        };
    }
    return {
        path: `/${LiveViewTabs.Riders}`,
        query: {}
    };
};

export const getLiveViewPath = ({
    clientId,
    queryParams = {},
    liveViewVisibilitySettings = {
        isOrderTabEnabled: true,
        isTourTabEnabled: true,
        enforceNewLiveView: false
    }
}: {
    clientId: string,
    queryParams?: { [string]: string },
    liveViewVisibilitySettings: {
        isOrderTabEnabled: boolean,
        isTourTabEnabled: boolean,
        enforceNewLiveView: boolean
    }
}): string => {
    const {
        isOrderTabEnabled,
        isTourTabEnabled,
        enforceNewLiveView
    } = liveViewVisibilitySettings;
    if (
        hasUserUpgradedToV2({
            enforceNewLiveView
        })
    ) {
        let { path, query } = getDefaultLiveViewTab({
            isOrderTabEnabled,
            isTourTabEnabled
        });
        if (queryParams) {
            query = {
                ...query,
                showBy: queryParams.showBy,
                selectedDate: queryParams.selectedDate
            };
        }
        return `/client/${clientId}/live_view${path}?${queryString.stringify(
            query
        )}`;
    }
    return `/client/${clientId}/live_view`;
};

export const getSingleTripScreenPath = ({
    clientId,
    location,
    queryParams
}: {
    clientId: string,
    queryParams: { [string]: string },
    location?: Location
}) => {
    const searchParams = new URLSearchParams(location?.search);
    const params = {
        ...queryParams,
        teamIds: location?.query?.teamIds || searchParams.get('teamIds'),
        showBy: location?.query?.showBy || searchParams.get('showBy')
    };
    if (
        !queryParams.date &&
        (location?.query?.date || searchParams.get('date'))
    ) {
        params.date = location?.query?.date || searchParams.get('date');
    }
    const pathname = `/client/${clientId}/live_view/tour`;
    const search = `?${queryString.stringify(params)}&view=${
        TripViews.Timeline
    }`;
    return { pathname, search };
};

export const getLiveViewIdsByRenderedScreen = (location: Location): Object => {
    if (!location) {
        return {};
    }
    const { pathname, query } = location;
    if (pathname.includes('live_view/task')) {
        return liveViewIds.v2[SCREENS.TASK_TABLE];
    }

    if (pathname.includes('live_view/tours') && query.view === 'timeline') {
        return liveViewIds.v2[SCREENS.TOUR_TIMELINE];
    }

    if (pathname.includes('live_view/tours')) {
        return liveViewIds.v2[SCREENS.TOUR_DETAIL];
    }

    if (pathname.includes('live_view/tour') && query.view === 'tasks') {
        return liveViewIds.v2[SCREENS.SINGLE_TOUR_DETAIL];
    }

    if (pathname.includes('live_view/tour') && query.view === 'timeline') {
        return liveViewIds.v2[SCREENS.SINGLE_TOUR_TIMELINE];
    }

    if (pathname.includes('live_view/riders')) {
        return liveViewIds.v2[SCREENS.RIDER_TABLE];
    }
};
